import Axios from 'axios'
import config from './config'
import { callAuthenticatedApi } from './authenticated-api'

Axios.defaults.withCredentials = true

const apiUrls = {
  remote: config.authRemoteApiUrl,
  local: config.authLocalApiUrl
}

const apiError = (err, fallbackMessage) => err.response?.data ?? { ok: false, error: { message: fallbackMessage } }

const checkLoggedIn = async ({ apiSource }) => {
  try {
    let options = {
      method: 'GET',
      url: `${apiUrls[apiSource]}/checkLoginToken`
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error verifying login")
  }
}

const login = async ({ apiSource, email, password }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/login`,
      data: {
        email,
        password,
      }
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during login")
  }
}

const logout = async ({ apiSource }) => {
  try {
    let options = {
      method: 'GET',
      url: `${apiUrls[apiSource]}/logout`
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during logout")
  }
}

const redeemSupportLogin = async ({ apiSource, token }) => {
  try {
    const response = await Axios({
      method: 'POST',
      url: `${apiUrls[apiSource]}/support-login`,
      data: { token }
    })

    return response.data
  } catch (err) {
    console.error(err)
    return apiError(err, 'Error using support login link')
  }
}

const signup = async ({ apiSource, email, password, name }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/signup`,
      data: {
        email,
        password,
        name,
      }
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during signup")
  }
}

const oauthLogin = async ({ apiSource, token }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/oauth/login`,
      data: { token }
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during login")
  }
}

const googleSigninStart = async ({ apiSource }) => {
  try {
    let options = {
      method: 'GET',
      url: `${apiUrls[apiSource]}/oauth/googleSigninStart`
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during login")
  }
}

const appleSigninStart = async ({ apiSource }) => {
  try {
    let options = {
      method: 'GET',
      url: `${apiUrls[apiSource]}/oauth/appleSigninStart`,
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during login")
  }
}

const linkedinSigninStart = async ({ apiSource }) => {
  try {
    let options = {
      method: 'GET',
      url: `${apiUrls[apiSource]}/oauth/linkedinSigninStart`
    }

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error during login")
  }
}


const checkEmailAvailability = async ({ apiSource, apiKey, email }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/oauth/checkEmailAvailability`,
      data: { email }
    }

    let data = await callApi({ apiKey, options })

    return data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error checking email availability")
  }
}

const requestEmailVerificationCode = async ({ apiSource, apiKey, email, user_id, username }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/oauth/requestEmailVerification`,
      data: { email, user_id, username }
    }

    let data = await callApi({ apiKey, options })

    return data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error starting email verification")
  }
}

const verifyEmailCode = async ({ apiSource, apiKey, user_id, code }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/oauth/verifyEmailCode`,
      data: { code, user_id }
    }

    let data = await callApi({ apiKey, options })

    return data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error verifying email code")
  }
}


const resetUserPasswordRequest = async ({ apiSource,email }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/password/resetUserPasswordRequest`,
      method: "put",
      data: { email }
    }

    let result = await Axios(options)

    return result.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error resetting password")
  }
}

const verifyChangePasswordRequest = async ({ apiSource,token }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/password/verifyChangePasswordRequest/` + token,
      method: "get"
    }

    let result = await Axios(options)

    return result.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error verifying password change request")
  }
}


const updateUserPassword = async ({ apiSource, apiKey, token, password }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/password/updateUserPassword`,
      method: "patch",
      data: {
        ...(token ? { token } : {}),
        password
      }
    }

    let result = apiKey
      ? await callApi({ apiKey, options })
      : (await Axios(options)).data

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error updating password")
  }
}


const acceptInviteAndLogin = async ({ apiSource, password, token, providerToken }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/signup/acceptInviteAndLogin`,
      method: "put",
      data: {
        password,
        token,
        providerToken
      }
    }

    let result = await Axios(options)

    return result.data
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error accepting invite")
  }
}

const callApi = async ({ apiKey, options }) => {
  try {
    return await callAuthenticatedApi({
      apiSource: getApiSourceFromUrl(options.url),
      apiKey,
      includeChildClientId: false,
      options
    })
  }
  catch (err) {
    throw err
  }
}

const getApiSourceFromUrl = url => {
  return url?.startsWith(apiUrls.local) ? 'local' : 'remote'
}

export {
  acceptInviteAndLogin,
  checkLoggedIn,
  googleSigninStart,
  appleSigninStart,
  linkedinSigninStart,
  login,
  logout,
  redeemSupportLogin,
  oauthLogin,
  checkEmailAvailability,
  requestEmailVerificationCode,
  resetUserPasswordRequest,
  signup,
  updateUserPassword,
  verifyEmailCode,
  verifyChangePasswordRequest,
}
